import { memo, type MouseEvent } from "react";
import type { InventoryItem, EquipmentSlotKey, SlotLocation } from "../types";
import { locationsEqual } from "../utils/location";
import { SLOT_ICONS } from "../equipmentConfig";
import { useEquipmentSlotSize } from "../hooks/useEquipmentSlotSize";
import { SectionLabel } from "./SectionLabel";
import { ItemSlot } from "./ItemSlot";
import characterIcon from "../assets/images/character-icon.webp";
import characterImage from "../assets/images/character.webp";

interface Props {
  equipment: Partial<Record<EquipmentSlotKey, InventoryItem>>;
  selectedLocation?: SlotLocation | null;
  onItemClick: (item: InventoryItem, location?: SlotLocation) => void;
  onItemRightClick: (
    e: MouseEvent,
    item: InventoryItem,
    location?: SlotLocation,
  ) => void;
}

export const CharacterItems = memo(function CharacterItems({
  equipment,
  selectedLocation,
  onItemClick,
  onItemRightClick,
}: Props) {
  const { S, GAP, charMaxW } = useEquipmentSlotSize();

  const slot = (key: EquipmentSlotKey) => {
    const loc: SlotLocation = { section: "equipment", key };
    return (
      <ItemSlot
        item={equipment[key]}
        location={loc}
        slotIcon={SLOT_ICONS[key]}
        isActive={!!selectedLocation && locationsEqual(selectedLocation, loc)}
        onClick={onItemClick}
        onRightClick={onItemRightClick}
      />
    );
  };

  return (
    <div className="flex flex-col gap-2 flex-1">
      <SectionLabel
        icon={characterIcon}
        title="Character Items"
        //    subtitle="here you keeping your stuff"
      />

      {/*
        7-column grid:
          col 1 = left-outer  (hat, mask)
          col 2 = left-inner  (glasses, jacket, tie, torso, pants)
          col 3 = gap column  (16px, no slots)
          col 4 = center      (character + shoes)
          col 5 = gap column  (16px, no slots)
          col 6 = right-inner (gloves, necklace, phone, bag s, bag l)
          col 7 = right-outer (armor, watch)
      */}
      <div
        className="grid gap-1"
        style={{
          gridTemplateColumns: `${S}px ${S}px ${GAP}px 1fr ${GAP}px ${S}px ${S}px`,
          gridTemplateRows: `repeat(5, ${S}px)`,
        }}
      >
        {/* Left outer (col 1) */}
        <div style={{ gridColumn: 1, gridRow: 1 }}>{slot("hat")}</div>
        <div style={{ gridColumn: 1, gridRow: 2 }}>{slot("mask")}</div>

        {/* Left inner (col 2) */}
        <div style={{ gridColumn: 2, gridRow: 1 }}>{slot("glasses")}</div>
        <div style={{ gridColumn: 2, gridRow: 2 }}>{slot("jacket")}</div>
        <div style={{ gridColumn: 2, gridRow: 3 }}>{slot("tie")}</div>
        <div style={{ gridColumn: 2, gridRow: 4 }}>{slot("torso")}</div>
        <div style={{ gridColumn: 2, gridRow: 5 }}>{slot("pants")}</div>

        {/* Center (col 4): character rows 1-4, shoes row 5 */}
        <div
          style={{ gridColumn: 4, gridRow: "1 / span 4" }}
          className="flex items-end justify-center overflow-hidden"
        >
          <img
            src={characterImage}
            alt="Character"
            className="object-contain h-full w-full"
            style={{ maxWidth: charMaxW }}
            draggable={false}
          />
        </div>
        <div
          style={{ gridColumn: 4, gridRow: 5 }}
          className="flex justify-center"
        >
          <div style={{ width: S, height: S }}>{slot("shoes")}</div>
        </div>

        {/* Right inner (col 6) */}
        <div style={{ gridColumn: 6, gridRow: 1 }}>{slot("gloves")}</div>
        <div style={{ gridColumn: 6, gridRow: 2 }}>{slot("necklace")}</div>
        <div style={{ gridColumn: 6, gridRow: 3 }}>{slot("phone")}</div>
        <div style={{ gridColumn: 6, gridRow: 4 }}>{slot("backpackSmall")}</div>
        <div style={{ gridColumn: 6, gridRow: 5 }}>{slot("backpackLarge")}</div>

        {/* Right outer (col 7) */}
        <div style={{ gridColumn: 7, gridRow: 1 }}>{slot("armor")}</div>
        <div style={{ gridColumn: 7, gridRow: 2 }}>{slot("watch")}</div>
      </div>
    </div>
  );
});
